import { omit, pick } from "lodash-es";
import { CustomMutationInput } from "@zaprooecommerce/magento-sdk/lib/methods";
import { NotificationType } from "@zaprooecommerce/storefront-ui-vue";
import { SpecialOrderRequestFile, SpecialOrderRequestFileType } from "../types";
import {
  PreparedSpecialOrdersFormData,
  SpecialOrdersFormData,
} from "~/modules/special-orders/interfaces";
import { useFileReader, useLoading } from "~/modules/helpers/composables";
import { StrapiFieldType, StrapiSpecialOrders } from "~/types/strapi";
import {
  INPUT_FILE_TOTAL_SIZE,
  SpecialOrdersTypes,
} from "~/modules/special-orders/variables";
import {
  SendSpecialOrderRequestMutationInput,
  SendSpecialOrderRequestMutationResponse,
} from "~/modules/special-orders/extended-queries/sendSpecialOrderRequest/types";
import { STATIC_FIELDS } from "~/modules/special-orders/variables/StaticFields";

export function useSpecialOrdersSubmit(specialOrder: StrapiSpecialOrders) {
  const type = specialOrder.type;
  const { readAsBase64 } = useFileReader();
  const { customMutation } = useMagentoSdk();
  const { prepareMagentoSdkOptionsWithRecaptcha } = useRecaptcha(
    `submit${SpecialOrdersTypes[type]}`,
  );
  const { loading, call: sendRequest } = useLoading(send);
  const { wrapCallbackToHandleError } = errorHandler();
  const { sendNotification } = useZaprooNotification();
  const { t } = useI18n();

  async function prepareFiles(
    files: File[] | undefined,
    type: SpecialOrderRequestFileType,
  ): Promise<SpecialOrderRequestFile[]> {
    if (!files) {
      return [];
    }

    return await Promise.all(
      files.map(async (file) => {
        const base64Data = await readAsBase64(file);

        return {
          file_type: type,
          mime_type: file.type,
          file_name: file.name,
          base64_file: base64Data.slice(base64Data.indexOf(",") + 1),
        };
      }),
    );
  }

  async function send(form: SpecialOrdersFormData): Promise<void> {
    return await wrapCallbackToHandleError(async () => {
      const preparedFormData = prepareFormDataField(form);

      // TODO remove this
      console.info(preparedFormData);

      const res = await customMutation<
        SendSpecialOrderRequestMutationResponse,
        CustomMutationInput<SendSpecialOrderRequestMutationInput["variables"]>
      >(
        {
          mutationName: "sendSpecialOrderRequest",
          mutationVariables: {
            input: {
              title: specialOrder.title,
              order_type: SpecialOrdersTypes[type],
              post_data: JSON.stringify(preparedFormData),
              files: ([] as SpecialOrderRequestFile[]).concat(
                ...(await Promise.all([
                  prepareFiles(
                    pick(form, ["typeFile"])?.typeFile,
                    SpecialOrderRequestFileType.type_file,
                  ),
                  prepareFiles(
                    pick(form, ["designFile"])?.designFile,
                    SpecialOrderRequestFileType.print_file,
                  ),
                ])),
              ),
            },
          },
        },
        {
          ...(await prepareMagentoSdkOptionsWithRecaptcha()),
          clientConfig: { baseURL: SDK_VSF_CONFIG.specialOrderClientApiUrl },
        },
      ).catch((err) => {
        throw new Error(httpErrorMessage(err));
      });

      throwGqlError(res);

      const status = res.data?.sendSpecialOrderRequest.status;

      if (status) {
        sendNotification({
          type: NotificationType.success,
          text: t("Query was sent successfully."),
        });
      } else {
        throw new Error(
          "Form is not submitted, status is not success. Please try again later",
        );
      }
    });
  }

  function httpErrorMessage(err: { response?: { status?: number } }): string {
    if (err.response?.status === 413) {
      return `${t("File size total is too big")}. ${t("Maximum size is {n}MB", {
        n: +(INPUT_FILE_TOTAL_SIZE / 1024).toFixed(2),
      })}`;
    }

    return t("Please try again later.");
  }

  function prepareFormDataField(
    data: SpecialOrdersFormData,
  ): PreparedSpecialOrdersFormData {
    const fieldsCollection = [...STATIC_FIELDS, ...specialOrder.fields];

    return Object.entries(omit(data, ["typeFile", "designFile"])).reduce(
      (
        preparedData: PreparedSpecialOrdersFormData,
        entry: [string, unknown],
      ) => {
        const field = fieldsCollection.find((f) => f.uid === entry[0]);

        if (!field) {
          throw new Error(`Field with key ${entry[0]} is not found`);
        }

        let valueLabel = "";

        if (field.type === StrapiFieldType.select) {
          valueLabel =
            (field.options || [])?.find((el) => el.value === entry[1])?.title ||
            entry[1]?.toString() ||
            "";
        } else if (field.type === StrapiFieldType.checkboxGroup) {
          const value = entry[1] as string[];
          valueLabel = (field.options || [])
            ?.filter((el) => value.includes(el.value))
            .map((o) => o.title)
            .join(", ");
        } else {
          valueLabel = entry[1]?.toString() || "";
        }

        preparedData[entry[0]] = {
          label: field.title,
          value_key: entry[1]?.toString() || "",
          value_label: valueLabel || entry[1]?.toString() || "",
        };

        return preparedData;
      },
      {},
    );
  }

  return {
    sendRequest,
    loading,
  };
}
